import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Alert from '@uikit/Alert/Alert';
import Input from '@uikit/Form/Input/Input';
import { Button } from '@uikit/Form/Button/Button';
import {
  sendPublisherWebsiteVerificationEmailApi,
  verifyPublisherWebsiteApi,
} from '@api/publishers.api';
import { PublisherWebsiteVerificationType } from 'respona_api/generated/common_pb';
import { PublisherWebsiteVerificationsReasonType } from '@ts/publishers.types';
import NumberInput from '@uikit/Form/NumberInput/NumberInput';
import { addNotification } from '@redux/actions/notifications.actions';

import './EmailVerification.scss';

export default function EmailVerification({
  websiteId,
  domain,
  handleResponse,
}: {
  websiteId: number;
  domain: string;
  handleResponse: (response: PublisherWebsiteVerificationsReasonType) => void;
}): JSX.Element {
  const [isEmailError, setIsEmailError] = useState(false);
  const [isVerificationCodeError, setIsVerificationCodeError] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState<number>();
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();

  const handleSendVerificationEmail = (notification?: boolean) => {
    // Every character of the domain is literal (a bare "." would match any character), and
    // email domains are case-insensitive.
    const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const emailRegex = new RegExp(`^[^\\s@]+@${escapedDomain}$`, 'i');

    if (!emailRegex.test(email)) {
      setIsEmailError(true);
      return;
    }

    setIsEmailError(false);
    sendPublisherWebsiteVerificationEmailApi(websiteId, email)
      .then(() => {
        setStep(2);

        if (notification) {
          dispatch(addNotification({ title: 'Code resent', type: 'success' }));
        }
      })
      .catch(() => {
        // request() already showed the server's error; stay on the email step so it can be fixed.
      });
  };

  const handleVerifyCode = () => {
    verifyPublisherWebsiteApi(
      Number(websiteId),
      PublisherWebsiteVerificationType.EMAIL,
      verificationCode.toString(),
      email
    ).then((response: PublisherWebsiteVerificationsReasonType) => {
      handleResponse(response);

      if (response.success) {
        setStep(3);
        setIsVerificationCodeError(false);
      } else {
        setIsVerificationCodeError(true);
      }
    });
  };

  return (
    <div>
      {step !== 3 ? (
        <div className="verify-info">
          Verify ownership instantly by email. We&apos;ll send a verification code to an address on{' '}
          <strong>{domain}</strong>. Enter the code below to confirm you own this domain.
        </div>
      ) : null}
      {step === 1 ? (
        <>
          <div className="email-label">Email address on {domain}</div>
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value.trim())}
            placeholder={`admin@${domain}`}
            isError={isEmailError}
            isFullWidth
            data-testid="website-wizard-email"
          />
          {isEmailError ? (
            <Alert variant="error" testId="website-wizard-email-error">
              The email must be on the {domain} domain. For example, admin@{domain}.
            </Alert>
          ) : null}
          <Button
            className="send-btn"
            disabled={!email}
            iconSize={17}
            leftIcon="envelope"
            onClick={() => handleSendVerificationEmail()}
            data-testid="website-wizard-email-send"
          >
            Send verification email
          </Button>
        </>
      ) : null}
      {step === 2 ? (
        <>
          <Alert variant="success">Code sent to {email}</Alert>
          <div className="verification-code">Enter verification code</div>
          <NumberInput
            value={verificationCode}
            type="number"
            onChange={(value) => setVerificationCode(Number(value))}
            placeholder="406513"
            disabledArrows
            isError={isVerificationCodeError}
            isFullWidth
            data-testid="website-wizard-email-code"
          />
          {isVerificationCodeError ? (
            <Alert variant="error">
              That code doesn&apos;t match. Double-check your email and try again.
            </Alert>
          ) : null}
          <Button
            className="send-btn"
            onClick={handleVerifyCode}
            disabled={!verificationCode}
            data-testid="website-wizard-email-verify"
          >
            Verify code
          </Button>
          <div className="verification-footer">
            <div className="blue-text" onClick={() => setStep(1)}>
              ← Change email address
            </div>
            <div className="grey-text" onClick={() => handleSendVerificationEmail(true)}>
              Resend code
            </div>
          </div>
        </>
      ) : null}
      {step === 3 ? (
        <Alert variant="success">
          <div>Domain verified</div>
          <div>
            {domain} is verified via {email}.
          </div>
        </Alert>
      ) : null}
    </div>
  );
}
